import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Chip,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityTwoTone from '@mui/icons-material/VisibilityTwoTone';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import _ from 'lodash';
import { postGeneric, apiConstants } from '../../../common/constants';
import { formatDate, formatCurrency } from '../../../helpers/UtilityHelper';
import { getStatusChipSx, getPaymentChipSx } from './cellStyles';

const OTP_LENGTH = 6;

const HISTORY_LIMIT = 100;
const excludes = 'sla,merchant,mappedOrderRule';

// ProshipOrder.currentStatus is written inconsistently by different backend
// code paths — sometimes the OrderStatusEnum constant name (e.g.
// "AWB_REGISTERED"), sometimes its human-readable `description` (e.g. "AWB
// registered on proship"). Only the enum-name form has a color defined in
// STATUS_COLORS (cellStyles.js), so resolve description text back to its
// enum name before rendering — mirrors order/.../enums/OrderStatusEnum.java.
const STATUS_DESCRIPTION_TO_ENUM = {
  'Unmapped status received from Courier': 'NO_STATUS_EXIST',
  'Order Has Been Placed / Manifested on Courier Partner': 'ORDER_PLACED',
  'Pickup Pending': 'PICKUP_PENDING',
  'Pickup Failed': 'PICKUP_FAILED',
  'Pickup Has Been Done': 'PICKED_UP',
  'In Transit': 'INTRANSIT',
  'Shipment Out For Delivery': 'OUT_FOR_DELIVERY',
  'Area For Delivery Is Not Servicable': 'NOT_SERVICEABLE',
  'Shipment Delivered': 'DELIVERED',
  'Delivery Failed': 'FAILED_DELIVERY',
  'Order Has Been Cancelled': 'CANCELLED_ORDER',
  'Rto For Shipment has been Requested': 'RTO_REQUESTED',
  'Marked As Return': 'RTO',
  'Shipment Is Out For Delivery For RTO': 'RTO_OUT_FOR_DELIVERY',
  'RTO Delivered': 'RTO_DELIVERED',
  'RTO Failed': 'RTO_FAILED',
  'Shipment is Lost': 'LOST',
  'Shipment is damaged': 'DAMAGED',
  'Shipment Is Delayed Or Misroute': 'SHIPMENT_DELAYED',
  'Contact To The Customer Care': 'CONTACT_CUSTOMER_CARE',
  'Shipment Is being held': 'SHIPMENT_HELD',
  'RTO In Transit': 'RTO_INTRANSIT',
  'Shipment Out For Pickup': 'OUT_FOR_PICKUP',
  'RTO Contact Customer Care': 'RTO_CONTACT_CUSTOMER_CARE',
  'RTO Shipment Delayed': 'RTO_SHIPMENT_DELAY',
  'AWB registered on proship': 'AWB_REGISTERED',
  'Exchange successful, Shipment delivered and picked up from the customer': 'EXCHANGE_PICKUP',
  'Exchanged shipment In Transit to warehouse': 'EXCHANGE_INTRANSIT',
  'When an exchange shipment is delivered back': 'EXCHANGE_DELIVERED',
  'When shipment is manifested': 'MANIFESTED',
  'Delivery partner is looking for the rider to whom the shipment has to be allocated.': 'RIDER_ALLOCATION_AWAITED',
  'Rider could not be found, hence the shipment could not be allocated to the rider.': 'RIDER_ALLOCATION_FAILED',
  'Shipment has been allocated to the rider successfully.': 'RIDER_ALLOCATED',
  'Rider has reached the pickup location from where the package has to be picked': 'REACHED_PICKUP_LOCATION',
  'Rider has reached the location where the package has to be dropped off': 'REACHED_DROP_LOCATION',
  'RTO-Delivered-Reschedule-Requested': 'RTO_DELIVERED_RESCHEDULE_REQ',
  'Shipment Reached At Destination': 'REACHED_AT_DESTINATION',
  'Shipment Shipped': 'SHIPPED',
  'Shipment is Missing': 'MISSING',
  'Shipment is Disposed Off': 'DISPOSED_OFF',
  'Cancellation pending on courier partner': 'CANCELLED_PENDING',
  'Cancellation rejected by courier partner': 'CANCELLATION_REJECTED',
  'Order already exists on CP': 'CP_ALREADY_EXISTS',
  'Order saved in Proship system': 'UNPROCESSED',
  'Order is under process': 'UNDER_PROCESS',
  'Custom Proship Event | Retrigger webhooks': 'RETRIGGER_WEBHOOKS',
  'Partially Delivered B2B Order': 'PARTIALLY_DELIVERED',
  'Request in processing': 'PROCESSING',
  'Processing Failed': 'PROCESSING_FAILED',
  'LRDelivered To Dc': 'DELIVERED_TO_DC',
  'Mother Bag Created for Airport': 'MOTHER_BAG_CREATED_AIR',
  'Ready To Dispatch For Airport': 'READY_TO_DISPATCH_FOR_AIRPORT',
  'Shipment Handed over At Origin Airport': 'HANDOVER_AT_ORIGIN_AIRPORT',
  'Shipment Arrived At Destination Airport': 'ARRIVED_AT_DESTINATION_AIRPORT',
  'Shipment arrived at warehouse from airport': 'ARRIVED_AT_WAREHOUSE_FROM_AIRPORT',
  'Pickup Cancelled By Courier': 'PICKUP_CANCELLED_BY_COURIER',
  'Pickup Cancelled By Shipper': 'PICKUP_CANCELLED_BY_SHIPPER',
  'RVP Cancelled QC Failed': 'RVP_CANCELLED_QC_FAILED',
  '--Remark--': 'IGNORE_STATUS',
};

const resolveShipmentStatus = (raw) => {
  if (!raw) return null;
  return STATUS_DESCRIPTION_TO_ENUM[raw.trim()] || raw;
};

// Shows a customer's past channel orders (matched by phone or email), capped
// at HISTORY_LIMIT. Fetches fresh on every open since `mobile`/`email` change
// per row clicked.
const CustomerOrderHistoryModal = ({ open, onClose, mobile, email, customerId, customerName, piiAccessAllow, merchantId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  // PII reveal (Show PII -> OTP -> unmask), mirroring PIIDetails.js's UX. We
  // already hold the customer's real phone/email client-side (masking here is
  // purely a display concern), so this only needs to re-verify the logged-in
  // user's identity via OTP — it deliberately does NOT call orderdata/getdata,
  // since that requires a real fulfilled shipment (ProshipContactInfo), which
  // has nothing to do with whether this customer's history should be visible.
  const [piiRevealed, setPiiRevealed] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [tokenId, setTokenId] = useState(null);
  const [otp, setOtp] = useState('');
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const piiReference = mobile || email || 'customer';

  const handleShowPii = async () => {
    try {
      setSendingOtp(true);
      const res = await postGeneric('orderdata/send-otp', {
        merchantOid: merchantId,
        reference: piiReference,
        awbNumber: 'CUSTOMER_PII',
      });
      setTokenId(res?.data?.tokenId);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to send OTP', { variant: 'error' });
    } finally {
      setSendingOtp(false);
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && value.length <= OTP_LENGTH)) setOtp(value);
  };

  const handleVerifyOtp = async () => {
    const trimmed = otp.trim();
    if (trimmed.length !== OTP_LENGTH) {
      enqueueSnackbar(`OTP must be ${OTP_LENGTH} digits`, { variant: 'error' });
      return;
    }
    if (!tokenId) {
      enqueueSnackbar('Token ID is missing', { variant: 'error' });
      return;
    }
    try {
      setVerifyingOtp(true);
      const res = await postGeneric('orderdata/verify-otp', {
        merchantOid: merchantId,
        reference: piiReference,
        awbNumber: 'CUSTOMER_PII',
        tokenId,
        otp: trimmed,
      });
      if (res?.data?.success) {
        setPiiRevealed(true);
        setTokenId(null);
        setOtp('');
        enqueueSnackbar('PII data retrieved successfully', { variant: 'success' });
      } else {
        enqueueSnackbar(res?.data?.message || 'Failed to verify OTP', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to verify OTP', { variant: 'error' });
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleCloseOtpModal = () => {
    setTokenId(null);
    setOtp('');
  };

  // `mobile`/`email` arrive here as whatever the row actually has — including
  // the backend's literal "*****" sentinel for a caller without PII access.
  // That sentinel is real, useful information for DISPLAY (it means "this
  // customer has a phone/email, you just can't see it"), but it must never
  // be used as a search VALUE (searching customer.phone == "*****" would
  // wrongly match every other PII-masked customer too). So: usable-for-search
  // and present-for-display are two different questions, answered separately.
  const isMasked = (v) => v === '*****';
  const hasUsableMobile = Boolean(mobile) && !isMasked(mobile);
  const hasUsableEmail = Boolean(email) && !isMasked(email);

  useEffect(() => {
    if (!open) return;
    // customer.id (the channel's own numeric customer id, e.g. Shopify's) is
    // never PII-masked server-side, unlike phone/email — for a user without
    // PII access, mobile/email are masked ("*****" — unusable as a search
    // value), so customerId is often the ONLY usable identity signal.
    // Without it, "no PII access" would also mean "can never see order
    // history", which isn't the intent.
    if (!hasUsableMobile && !hasUsableEmail && !customerId) {
      setOrders([]);
      setError('No phone number, email, or customer ID available for this customer.');
      return;
    }

    const orfilter = [];
    if (customerId) orfilter.push({ field: 'customer.id', operator: 'eq', value: customerId });
    if (hasUsableMobile) orfilter.push({ field: 'customer.phone', operator: 'eq', value: mobile });
    if (hasUsableEmail) orfilter.push({ field: 'customer.email', operator: 'eq', value: email });

    setError(null);
    setIsLoading(true);
    postGeneric(apiConstants.CHANNEL_ORDER_SEARCh, {
      orfilter,
      limit: HISTORY_LIMIT,
      skipCount: true,
      excludes,
      sort: { field: 'createdDate', direction: 'DESC' },
    })
      .then((res) => setOrders(res.data?.data || []))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load order history.'))
      .finally(() => setIsLoading(false));
  }, [open, mobile, email, customerId]);

  const handleClose = () => {
    setOrders([]);
    setError(null);
    // Reset the reveal — masking must come back on next open, same as
    // PIIDetails.js (component-local state only, nothing persisted).
    setPiiRevealed(false);
    setTokenId(null);
    setOtp('');
    onClose();
  };

  // Always masked by default here, same as PIIDetails.js on the order/shipment
  // detail page — reveal depends purely on the logged-in user's own PII
  // access (piiAccessAllow), not on any merchant-level report/consent flag.
  // (The `maskPii` prop — derived from shouldMaskData — already returns false
  // whenever piiAccessAllow is true, so gating the reveal button on BOTH would
  // make it unreachable; masking and the reveal-button both key off
  // piiAccessAllow alone.)
  const shouldMask = !piiRevealed;
  // Fully masked (no real characters shown at all) — matches PIIDetails.js's
  // fixed placeholders exactly. Deliberately NOT maskPhoneNumber/maskEmail,
  // which keep the last 4 characters visible; that's fine for a grid-level
  // partial reveal elsewhere in the app, but defeats the point of gating a
  // full reveal behind OTP here.
  const displayMobile = shouldMask ? (mobile ? '**********' : null) : mobile;
  const displayEmail = shouldMask ? (email ? '*********' : null) : email;
  const canRevealPii = piiAccessAllow && !piiRevealed && (mobile || email);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth scroll="body">
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', pr: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Order History</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              {customerName ? `${customerName} — ` : ''}
              {[displayMobile, displayEmail].filter(Boolean).join(' · ') || 'No contact details'}
            </Typography>
            {canRevealPii && (
              <LoadingButton
                onClick={handleShowPii}
                loading={sendingOtp}
                color="error"
                size="small"
                variant="contained"
                startIcon={<VisibilityTwoTone />}
                sx={{ py: 0.2 }}
              >
                Show PII
              </LoadingButton>
            )}
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Looking up order history…
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">{error}</Typography>
          </Box>
        ) : orders.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No past orders found for this customer.</Typography>
          </Box>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Showing {orders.length}{orders.length === HISTORY_LIMIT ? '+' : ''} order{orders.length === 1 ? '' : 's'}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order No</TableCell>
                    <TableCell>Store</TableCell>
                    <TableCell>Order Date</TableCell>
                    <TableCell>Order Status</TableCell>
                    <TableCell>Shipment Status</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Items</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.id || order.channelOrderId} hover>
                      <TableCell>{order.order_number || order.orderName || '-'}</TableCell>
                      <TableCell>{order.store_name || '-'}</TableCell>
                      <TableCell>{order.createdDate ? formatDate(order.createdDate, 'dd MMM, yyyy') : '-'}</TableCell>
                      <TableCell>
                        {order.channelOrderStatus
                          ? <Chip size="small" variant="outlined" label={_.startCase(order.channelOrderStatus)} sx={getStatusChipSx(order.channelOrderStatus)} />
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {order.proshipOrder?.length
                          ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {order.proshipOrder.map((p, i) => {
                                const status = resolveShipmentStatus(p?.currentStatus);
                                // Real line-item link: each order item records which AWB
                                // fulfilled it (fullfilled_against_awb) — that's the source
                                // of truth, NOT this shipment's position in the array.
                                const matchedItemNames = (order.orderItems || [])
                                  .filter((item) => item?.fullfilled_against_awb && item.fullfilled_against_awb === p?.awbNumber)
                                  .map((item) => item.title || item.variant_title || 'Item');
                                const label = matchedItemNames.length
                                  ? `(${matchedItemNames.join(', ')}) ${status || '-'}`
                                  : `(Shipment ${i + 1}) ${status || '-'}`;
                                return (
                                  <Chip
                                    key={p?.awbNumber || i}
                                    size="small"
                                    variant="outlined"
                                    label={label}
                                    sx={getStatusChipSx(status)}
                                  />
                                );
                              })}
                            </Box>
                          )
                          : <Chip size="small" variant="outlined" label="Shipment Not Created" sx={getStatusChipSx()} />}
                      </TableCell>
                      <TableCell>
                        {order.payment_mode
                          ? <Chip size="small" variant="outlined" label={order.payment_mode} sx={getPaymentChipSx(order.payment_mode)} />
                          : '-'}
                      </TableCell>
                      <TableCell align="right">{order.total_price ? formatCurrency(order.total_price) : '-'}</TableCell>
                      <TableCell align="right">{order.orderItems?.length || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">Close</Button>
      </DialogActions>

      <Dialog open={tokenId !== null} onClose={handleCloseOtpModal} maxWidth="xs" fullWidth>
        <DialogTitle>Enter OTP</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please enter the {OTP_LENGTH}-digit OTP sent to your registered phone number to view PII details.
          </Typography>
          <TextField
            fullWidth
            label="OTP"
            value={otp}
            onChange={handleOtpChange}
            placeholder={`Enter ${OTP_LENGTH}-digit OTP`}
            variant="outlined"
            type="tel"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            inputProps={{ pattern: '[0-9]*', maxLength: OTP_LENGTH }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !verifyingOtp && otp.trim().length === OTP_LENGTH) handleVerifyOtp();
            }}
            helperText={otp.length > 0 && otp.length < OTP_LENGTH ? `${OTP_LENGTH - otp.length} digit(s) remaining` : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOtpModal} color="inherit">Cancel</Button>
          <LoadingButton
            onClick={handleVerifyOtp}
            loading={verifyingOtp}
            variant="contained"
            color="primary"
            disabled={otp.trim().length !== OTP_LENGTH}
          >
            Verify OTP
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CustomerOrderHistoryModal;
